// The pyro plan: effects and counts, checked against the pyro drones booked.

import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../components/Toast/ToastContext';
import { Button, IconButton } from '../components/ui/Button';
import Field from '../components/ui/Field';
import Panel from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import Tooltip from '../components/ui/Tooltip';
import { IconCheck, IconPlus } from '../components/ui/icons';
import { useFlip, useTicker } from '../components/ui/motion';
import { setPyro } from './sparkApi';
import type { ProductionDetail } from './sparkApi';
import { useBecame } from './SparkProps';
import { errorMessage } from './sparkUi';

type Line = { effect: string; count: number };

const IconMinus: React.FC<{ className?: string }> = ({ className = 'h-4 w-4' }) => (
  <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" className={className}>
    <rect x="4" y="10.8" width="16" height="2.4" rx="1.2" fill="currentColor" />
  </svg>
);

/** A count in a recessed well, with a key either side. */
const Stepper: React.FC<{ line: Line; onChange: (count: number) => void; disabled: boolean }> = ({ line, onChange, disabled }) => {
  const shown = useTicker(line.count);
  return (
    <span className="inline-flex flex-none items-center gap-0.5 rounded-control bg-surface-container-high p-0.5 shadow-recess">
      <Tooltip label={line.count === 1 ? 'Remove this effect' : 'One fewer'}>
        <IconButton size="sm" disabled={disabled} label={`One fewer ${line.effect}`} onClick={() => onChange(line.count - 1)}>
          <IconMinus className="h-3.5 w-3.5" />
        </IconButton>
      </Tooltip>
      <span aria-hidden="true" className="min-w-[2rem] text-center text-sm tabular-nums text-on-surface">{shown}</span>
      <span className="sr-only" aria-live="polite">{`${line.count} ${line.effect}`}</span>
      <Tooltip label="One more">
        <IconButton size="sm" disabled={disabled} label={`One more ${line.effect}`} onClick={() => onChange(line.count + 1)}>
          <IconPlus className="h-3.5 w-3.5" />
        </IconButton>
      </Tooltip>
    </span>
  );
};

const SparkPyroPanel: React.FC<{ detail: ProductionDetail; studio: boolean; onChanged: () => void }> = ({ detail, studio, onChanged }) => {
  const toast = useToast();
  const saved = detail.pyro.map((l) => ({ effect: l.effect, count: l.count }));
  const [lines, setLines] = useState<Line[]>(saved);
  const [adding, setAdding] = useState('');
  const [busy, setBusy] = useState(false);
  const list = useRef<HTMLUListElement>(null);
  const savedKey = JSON.stringify(saved);
  // A live refetch must not wipe a draft in progress: only a clean plan follows the server. Discard takes theirs.
  const adopted = useRef(savedKey);
  useEffect(() => {
    if (JSON.stringify(lines) === adopted.current) setLines(JSON.parse(savedKey));
    adopted.current = savedKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);
  useFlip(list, [lines.map((l) => l.effect).join('|')]);

  const booked = detail.production.pyro_drone_count;
  const total = lines.reduce((m, l) => m + l.count, 0);
  const shownTotal = useTicker(total);
  const dirty = JSON.stringify(lines) !== savedKey;
  const diff = booked === null || booked === undefined ? null : total - booked;
  const matched = useBecame(diff === 0 && lines.length > 0);

  const save = async () => {
    setBusy(true);
    try {
      await setPyro(detail.production.id, lines.filter((l) => l.count > 0));
      toast.success('Pyro plan saved.');
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e, 'Could not save the pyro plan. Try again.'));
    }
    setBusy(false);
  };

  const add = (e: React.FormEvent) => {
    e.preventDefault();
    const name = adding.trim();
    if (!name || lines.some((l) => l.effect.toLowerCase() === name.toLowerCase())) return;
    setLines([...lines, { effect: name, count: 1 }]);
    setAdding('');
  };

  if (!studio && lines.length === 0 && !booked) return null;

  return (
    <Panel title="Pyro plan">
      <p className="m-0 -mt-1.5 text-sm text-on-surface-variant">
        {booked ? `${booked} pyro drones are booked on the event. Packing sees this plan.` : 'No pyro drones are booked. If the producer adds some, plan the effects here.'}
      </p>

      <Reveal show={lines.length > 0}>
        <ul ref={list} className="m-0 mt-3 list-none space-y-1 p-0">
          {lines.map((l, i) => (
            <li key={l.effect} data-flip-key={l.effect} className="flex min-h-10 items-center justify-between gap-3 rounded-control bg-surface-container py-1 pl-3 pr-1">
              <span className="min-w-0 truncate text-sm text-on-surface">{l.effect}</span>
              {studio ? (
                <Stepper
                  line={l}
                  disabled={busy}
                  onChange={(count) => setLines(lines.map((x, j) => (j === i ? { ...x, count: Math.max(0, count) } : x)).filter((x, j) => j !== i || x.count > 0))}
                />
              ) : (
                <span className="pr-2 text-sm tabular-nums text-on-surface">{l.count}</span>
              )}
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between px-3 pt-2 text-sm text-on-surface-variant">
          <span>Planned</span>
          <span className="tabular-nums text-on-surface">{shownTotal}</span>
        </div>
      </Reveal>

      <Reveal show={diff !== null && lines.length > 0}>
        <p
          role="status"
          className={`m-0 mt-2 flex items-center gap-1.5 text-sm transition-[color] duration-standard ease-emphasized ${diff === 0 ? 'text-green-300' : 'text-orange-300'}`}
        >
          {diff === 0
            ? <><IconCheck className="h-3.5 w-3.5 flex-none" draw={matched} />Matches the {booked} pyro drones booked.</>
            : `${Math.abs(diff ?? 0)} ${(diff ?? 0) < 0 ? 'short of' : 'over'} the ${booked} pyro drones booked.`}
        </p>
      </Reveal>

      {studio && (
        <form onSubmit={add} className="mt-3 flex flex-wrap items-center gap-2">
          <Field
            aria-label="Add a pyro effect"
            value={adding}
            maxLength={80}
            onChange={(e) => setAdding(e.target.value)}
            placeholder="Add an effect, like amber gerb"
            className="min-w-[10rem] flex-1"
          />
          <Button type="submit" disabled={!adding.trim()} leading={<IconPlus className="h-3.5 w-3.5" />}>Add effect</Button>
        </form>
      )}
      <Reveal show={studio && dirty}>
        <div className="flex flex-wrap gap-2 p-0.5 pt-3">
          <Button variant="filled" disabled={busy} onClick={save}>Save pyro plan</Button>
          <Button variant="quiet" disabled={busy} onClick={() => setLines(saved)}>Discard changes</Button>
        </div>
      </Reveal>
    </Panel>
  );
};

export default SparkPyroPanel;
